<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import FunnelForm from './FunnelForm.vue';
import PermissionsModal from './PermissionsModal.vue';

const { t } = useI18n();
const store = useStore();

const showAddModal = ref(false);
const showEditModal = ref(false);
const showDuplicateModal = ref(false);
const showPermissionsModal = ref(false);
const showDeleteConfirm = ref(false);
const currentFunnel = ref(null);
const duplicateFunnel = ref(null);

const funnels = useMapGetter('funnels/getFunnels');
const uiFlags = useMapGetter('funnels/getUIFlags');
const currentUser = useMapGetter('getCurrentUser');
const currentRole = useMapGetter('getCurrentRole');

const isAdmin = computed(() => currentRole.value === 'administrator');

onMounted(() => {
  store.dispatch('funnels/get');
});

const canEdit = funnel => {
  return isAdmin.value || funnel.created_by?.id === currentUser.value.id;
};

const onSaveFunnel = async ({ id, funnel, shared_with }) => {
  try {
    if (id) {
      await store.dispatch('funnels/update', { id, funnel, shared_with });
      useAlert(t('CRM.SETTINGS.FUNNEL_UPDATED'));
    } else {
      await store.dispatch('funnels/create', { funnel, shared_with });
      useAlert(t('CRM.SETTINGS.FUNNEL_CREATED'));
    }
    showAddModal.value = false;
    showEditModal.value = false;
  } catch {
    useAlert(t('CRM.SETTINGS.FUNNEL_SAVE_ERROR'));
  }
};

const openEditModal = funnel => {
  currentFunnel.value = funnel;
  showEditModal.value = true;
};

const openDuplicateModal = funnel => {
  duplicateFunnel.value = {
    name: `${funnel.name} (${t('CRM.SETTINGS.COPY')})`,
    description: funnel.description,
    funnel_stages: (funnel.funnel_stages || []).map((s, i) => ({
      name: s.name,
      color: s.color,
      position: i,
    })),
  };
  showDuplicateModal.value = true;
};

const openPermissions = funnel => {
  currentFunnel.value = funnel;
  showPermissionsModal.value = true;
};

const openDeleteConfirm = funnel => {
  currentFunnel.value = funnel;
  showDeleteConfirm.value = true;
};

const confirmDelete = async () => {
  try {
    await store.dispatch('funnels/delete', currentFunnel.value.id);
    useAlert(t('CRM.SETTINGS.FUNNEL_DELETED'));
  } catch {
    useAlert(t('CRM.SETTINGS.FUNNEL_DELETE_ERROR'));
  }
  showDeleteConfirm.value = false;
  currentFunnel.value = null;
};
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="t('CRM.LOADING')"
    :no-records-found="!funnels.length"
    :no-records-message="t('CRM.SETTINGS.NO_FUNNELS')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('CRM.SETTINGS.TITLE')"
        :description="t('CRM.SETTINGS.DESCRIPTION')"
        feature-name="crm"
      >
        <template v-if="funnels.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ funnels.length }}
            {{ funnels.length === 1 ? 'funnel' : 'funnels' }}
          </span>
        </template>
        <template #actions>
          <Button
            :label="t('CRM.SETTINGS.ADD_FUNNEL_BTN')"
            size="sm"
            @click="showAddModal = true"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div class="divide-y divide-n-weak border-t border-n-weak">
        <div
          v-for="funnel in funnels"
          :key="funnel.id"
          class="flex items-center justify-between gap-4 py-4 hover:bg-n-alpha-1 -mx-2 px-2 rounded-lg transition-colors"
        >
          <div class="flex items-start gap-3 min-w-0">
            <!-- Stage color dots -->
            <div class="flex items-center gap-0.5 mt-1.5 flex-shrink-0">
              <span
                v-for="stage in (funnel.funnel_stages || []).slice(0, 6)"
                :key="stage.id"
                class="size-2.5 rounded-full"
                :style="{ backgroundColor: stage.color }"
              />
            </div>
            <div class="flex flex-col gap-1 min-w-0">
              <span class="text-heading-3 text-n-slate-12">
                {{ funnel.name }}
              </span>
              <div
                class="flex items-center gap-2 text-body-main text-n-slate-11"
              >
                <span>
                  {{ funnel.funnel_stages?.length || 0 }}
                  {{ t('CRM.SETTINGS.STAGES_COUNT') }}
                </span>
                <div class="w-px h-3 bg-n-strong rounded-lg" />
                <span>
                  {{ t('CRM.SETTINGS.CREATED_BY') }}
                  {{ funnel.created_by?.name }}
                </span>
              </div>
            </div>
          </div>
          <div class="flex items-center gap-2 flex-shrink-0">
            <Button
              v-if="canEdit(funnel)"
              v-tooltip.top="t('CRM.PERMISSIONS.TITLE')"
              icon="i-lucide-shield"
              slate
              sm
              @click="openPermissions(funnel)"
            />
            <Button
              v-if="canEdit(funnel)"
              v-tooltip.top="t('CRM.SETTINGS.DUPLICATE_FUNNEL')"
              icon="i-lucide-copy"
              slate
              sm
              @click="openDuplicateModal(funnel)"
            />
            <Button
              v-if="canEdit(funnel)"
              v-tooltip.top="t('CRM.SETTINGS.EDIT_FUNNEL')"
              icon="i-woot-edit-pen"
              slate
              sm
              @click="openEditModal(funnel)"
            />
            <Button
              v-if="canEdit(funnel)"
              v-tooltip.top="t('CRM.SETTINGS.DELETE_FUNNEL')"
              icon="i-woot-bin"
              slate
              sm
              class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
              @click="openDeleteConfirm(funnel)"
            />
          </div>
        </div>
      </div>
    </template>

    <woot-modal
      v-model:show="showAddModal"
      :on-close="() => (showAddModal = false)"
    >
      <FunnelForm @close="showAddModal = false" @save="onSaveFunnel" />
    </woot-modal>

    <woot-modal
      v-model:show="showEditModal"
      :on-close="() => (showEditModal = false)"
    >
      <FunnelForm
        v-if="showEditModal"
        :funnel="currentFunnel"
        @close="showEditModal = false"
        @save="onSaveFunnel"
      />
    </woot-modal>

    <woot-modal
      v-model:show="showDuplicateModal"
      :on-close="() => (showDuplicateModal = false)"
    >
      <FunnelForm
        v-if="showDuplicateModal"
        :funnel="duplicateFunnel"
        @close="showDuplicateModal = false"
        @save="onSaveFunnel"
      />
    </woot-modal>

    <woot-modal
      v-model:show="showPermissionsModal"
      :on-close="() => (showPermissionsModal = false)"
    >
      <PermissionsModal
        v-if="showPermissionsModal"
        :funnel="currentFunnel"
        @close="showPermissionsModal = false"
      />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirm"
      :on-close="() => (showDeleteConfirm = false)"
      :on-confirm="confirmDelete"
      :title="t('CRM.SETTINGS.DELETE_TITLE')"
      :message="t('CRM.SETTINGS.DELETE_MESSAGE')"
      :confirm-text="t('CRM.SETTINGS.DELETE_CONFIRM')"
      :reject-text="t('CRM.SETTINGS.DELETE_CANCEL')"
    />
  </SettingsLayout>
</template>
